import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Flower, PaperPlaneRight, Image as ImageIcon, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import io from 'socket.io-client';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOCKET_URL = process.env.REACT_APP_BACKEND_URL;

export default function Chat() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, messagesRes] = await Promise.all([
          axios.get(`${API}/auth/me`, { withCredentials: true }),
          axios.get(`${API}/messages`, { withCredentials: true })
        ]);
        setUser(userRes.data);
        setMessages(messagesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();

    socketRef.current = io(SOCKET_URL);
    socketRef.current.on('new_message', (message) => {
      setMessages((prev) => [...prev, message]);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setSelectedFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadImage = async (file) => {
    try {
      const sigResponse = await axios.get(`${API}/cloudinary/signature?resource_type=image&folder=chat`, {
        withCredentials: true
      });
      const sig = sigResponse.data;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      const uploadResponse = await axios.post(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        formData
      );

      return uploadResponse.data.secure_url;
    } catch (error) {
      console.error('Image upload error:', error);
      throw error;
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !selectedFile) return;

    try {
      setUploading(true);
      let imageUrl = null;

      if (selectedFile) {
        imageUrl = await uploadImage(selectedFile);
      }

      await axios.post(
        `${API}/messages`,
        { text: newMessage.trim() || null, image_url: imageUrl },
        { withCredentials: true }
      );

      setNewMessage('');
      clearImage();
    } catch (error) {
      console.error('Send message error:', error);
      toast.error('Failed to send message');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      <nav className="bg-white/70 backdrop-blur-lg border-b border-white/20 px-6 py-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button 
            data-testid="back-to-dashboard-button"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            size="sm"
          >
            ← Back
          </Button>
          <Flower size={28} weight="fill" className="text-primary" />
          <span className="text-xl font-heading font-bold">Community Chat</span>
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
        {messages.map((msg, idx) => {
          const isMe = msg.user_id === user?.user_id;
          return (
            <motion.div
              key={msg.message_id || idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex gap-3 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}
              data-testid={`message-${idx}`}
            >
              <img 
                src={msg.user_picture} 
                alt={msg.user_name} 
                className="w-10 h-10 rounded-full flex-shrink-0"
              />
              <div className={`max-w-md ${isMe ? 'items-end' : 'items-start'} flex flex-col`}>
                <span className="text-xs text-muted-foreground font-medium mb-1">{msg.user_name}</span>
                <div className={`rounded-2xl px-4 py-3 ${isMe ? 'bg-primary text-white' : 'bg-white border border-border'}`}>
                  {msg.text && <p className="text-sm">{msg.text}</p>}
                  {msg.image_url && (
                    <img 
                      src={msg.image_url} 
                      alt="Shared" 
                      className="mt-2 rounded-xl max-w-full cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => window.open(msg.image_url, '_blank')}
                    />
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <div className="border-t border-border bg-white px-6 py-4">
        {imagePreview && (
          <div className="mb-3 relative inline-block">
            <img src={imagePreview} alt="Preview" className="h-20 rounded-lg" />
            <button
              onClick={clearImage}
              className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1 hover:bg-destructive/90"
            >
              <X size={16} />
            </button>
          </div>
        )}
        <form onSubmit={handleSendMessage} className="flex gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
          />
          <Button
            data-testid="upload-image-button"
            type="button"
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            size="icon"
            className="rounded-full flex-shrink-0 relative z-50"
            disabled={uploading}
          >
            <ImageIcon size={20} />
          </Button>
          <Input
            data-testid="message-input"
            type="text"
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            className="flex-1 rounded-xl"
            disabled={uploading}
          />
          <Button 
            data-testid="send-message-button"
            type="submit"
            className="rounded-full bg-primary hover:bg-primary-hover flex-shrink-0 relative z-50"
            disabled={uploading || (!newMessage.trim() && !selectedFile)}
          >
            {uploading ? '...' : <PaperPlaneRight size={20} weight="fill" />}
          </Button>
        </form>
      </div>
    </div>
  );
}